import {
  assertAuthorityRevisionSet,
  type AuthorityRevisionSet,
  type LineServiceMode,
  type ServiceMode,
} from '../contracts/operating-context.ts';
import type { OrderIntakeEnvelope } from './order-intake.ts';
import type { OrderLineSnapshot } from './product-selection.ts';

export interface AvailabilityLineEvidence {
  readonly productId: string;
  readonly status: 'available' | 'soldout' | 'paused';
}

export interface ValidationEvidence {
  readonly status: 'valid' | 'rejected';
  readonly menuRevision: string;
  readonly availabilityRevision: string;
  readonly rulesRevision: string;
  readonly availabilityLines?: readonly AvailabilityLineEvidence[];
}

export interface PricingLineEvidence {
  readonly lineId: string;
  readonly productId: string;
  readonly quantity: number;
  readonly unitBaseMinor: number;
  readonly unitModifierMinor: number;
  readonly lineSubtotalMinor: number;
}

export interface PricingConsumedUnit {
  readonly lineId: string;
  readonly unitIndex: number;
}

export interface PricingAdjustment {
  readonly kind: 'combo' | 'discount' | 'surcharge';
  readonly referenceId: string;
  readonly amountMinor: number;
  readonly consumedUnits?: readonly PricingConsumedUnit[];
}

export interface PricingEvidence {
  readonly status: 'quoted' | 'rejected';
  readonly pricingRevision: string;
  readonly currency: string;
  readonly totalMinor: number;
  readonly lines: readonly PricingLineEvidence[];
  readonly adjustments: readonly PricingAdjustment[];
}

export interface OrderFinalizationEvidence {
  readonly policyVersion: string;
  readonly validation: ValidationEvidence;
  readonly pricing: PricingEvidence;
}

export interface PriceDifferencePolicy {
  readonly mode: 'contact_customer' | 'store_may_accept_displayed';
  readonly maxAcceptedDifferenceMinor: number;
}

export interface PriceDifferenceDecision {
  readonly action: 'customer_confirmed_authority' | 'store_accepted_displayed';
  readonly operatorId: string;
  readonly resolvedAt: string;
  readonly reason?: string;
}

export interface PriceResolution {
  readonly customerDisplayedTotalMinor?: number;
  readonly authoritativeTotalMinor: number;
  readonly differenceMinor: number;
  readonly finalTotalMinor: number;
  readonly action: 'matched' | PriceDifferenceDecision['action'];
  readonly operatorId?: string;
  readonly resolvedAt?: string;
  readonly reason?: string;
}

export interface FinalizeOrderCommand {
  readonly orderId: string;
  readonly idempotencyKey: string;
  readonly createdAt: string;
  readonly customerId?: string;
  readonly pricePolicy?: PriceDifferencePolicy;
  readonly priceDecision?: PriceDifferenceDecision;
}

export interface CanonicalOrderSnapshot {
  readonly orderId: string;
  readonly intakeId: string;
  readonly idempotencyKey: string;
  readonly revision: 1;
  readonly status: 'accepted';
  readonly businessDate: string;
  readonly source: OrderIntakeEnvelope['source'];
  readonly channel: OrderIntakeEnvelope['channel'];
  readonly customerId?: string;
  readonly serviceMode?: ServiceMode;
  readonly lineServiceModes?: readonly LineServiceMode[];
  readonly lines: readonly OrderLineSnapshot[];
  readonly currency: string;
  readonly totalMinor: number;
  readonly pricingLines: readonly PricingLineEvidence[];
  readonly pricingAdjustments: readonly PricingAdjustment[];
  readonly priceResolution: PriceResolution;
  readonly authority: AuthorityRevisionSet;
  readonly createdAt: string;
}

interface StoredFinalization {
  readonly signature: string;
  readonly order: CanonicalOrderSnapshot;
}

const cloneLine = (line: OrderLineSnapshot): OrderLineSnapshot => ({
  ...line,
  selectorKeys: [...line.selectorKeys],
  productSnapshot: {
    ...line.productSnapshot,
    selectedOptions: line.productSnapshot.selectedOptions.map((option) => ({ ...option })),
  },
});

const cloneAdjustment = (adjustment: PricingAdjustment): PricingAdjustment => ({
  ...adjustment,
  ...(adjustment.consumedUnits === undefined
    ? {}
    : { consumedUnits: adjustment.consumedUnits.map((unit) => ({ ...unit })) }),
});

const cloneOrder = (order: CanonicalOrderSnapshot): CanonicalOrderSnapshot => ({
  ...order,
  lineServiceModes: order.lineServiceModes?.map((entry) => ({ ...entry })),
  lines: order.lines.map(cloneLine),
  pricingLines: order.pricingLines.map((line) => ({ ...line })),
  pricingAdjustments: order.pricingAdjustments.map(cloneAdjustment),
  priceResolution: { ...order.priceResolution },
  authority: { ...order.authority },
});

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, stableValue(entry)]),
    );
  }
  return value;
};

const signatureOf = (
  intake: OrderIntakeEnvelope,
  evidence: OrderFinalizationEvidence,
  command: FinalizeOrderCommand,
): string => JSON.stringify(stableValue({ intake, evidence, command }));

const assertAcceptedIntake = (intake: OrderIntakeEnvelope): void => {
  if (intake.status !== 'accepted') throw new Error('ORDER_INTAKE_NOT_ACCEPTED');
};

const authorityRevisionsFromEvidence = (evidence: OrderFinalizationEvidence): AuthorityRevisionSet => ({
  policyVersion: evidence.policyVersion,
  menuRevision: evidence.validation.menuRevision,
  availabilityRevision: evidence.validation.availabilityRevision,
  pricingRevision: evidence.pricing.pricingRevision,
  rulesRevision: evidence.validation.rulesRevision,
});

const assertAuthorityEvidence = (evidence: OrderFinalizationEvidence): void => {
  if (evidence.validation.status !== 'valid') throw new Error('ORDER_VALIDATION_REJECTED');
  assertAuthorityRevisionSet(authorityRevisionsFromEvidence(evidence));
  if (evidence.pricing.status !== 'quoted') throw new Error('ORDER_PRICING_NOT_QUOTED');
  if (!evidence.pricing.currency) throw new Error('ORDER_PRICING_CURRENCY_REQUIRED');
};

const assertAvailability = (intake: OrderIntakeEnvelope, validation: ValidationEvidence): void => {
  const blocked = new Map(
    (validation.availabilityLines ?? [])
      .filter((line) => line.status !== 'available')
      .map((line) => [line.productId, line.status]),
  );
  if (intake.lines.some((line) => blocked.has(line.productId))) {
    throw new Error('ORDER_SOLD_OUT_REQUIRES_REVIEW');
  }
};

const assertPricing = (intake: OrderIntakeEnvelope, pricing: PricingEvidence): void => {
  if (pricing.lines.length !== intake.lines.length) throw new Error('ORDER_PRICING_CARGO_MISMATCH');
  const pricingByLine = new Map(pricing.lines.map((line) => [line.lineId, line]));
  for (const intakeLine of intake.lines) {
    const priced = pricingByLine.get(intakeLine.lineId);
    if (!priced || priced.productId !== intakeLine.productId || priced.quantity !== intakeLine.quantity) {
      throw new Error('ORDER_PRICING_CARGO_MISMATCH');
    }
    if (!Number.isSafeInteger(priced.unitBaseMinor) || priced.unitBaseMinor < 0 ||
        !Number.isSafeInteger(priced.unitModifierMinor) ||
        !Number.isSafeInteger(priced.lineSubtotalMinor) ||
        priced.lineSubtotalMinor !== (priced.unitBaseMinor + priced.unitModifierMinor) * priced.quantity ||
        priced.lineSubtotalMinor < 0) {
      throw new Error('ORDER_PRICING_CARGO_MISMATCH');
    }
  }

  const intakeByLine = new Map(intake.lines.map((line) => [line.lineId, line]));
  for (const adjustment of pricing.adjustments) {
    if (!adjustment.referenceId || !Number.isSafeInteger(adjustment.amountMinor)) {
      throw new Error('ORDER_PRICING_ADJUSTMENT_INVALID');
    }
    for (const unit of adjustment.consumedUnits ?? []) {
      const line = intakeByLine.get(unit.lineId);
      if (!line || !Number.isSafeInteger(unit.unitIndex) || unit.unitIndex < 0 || unit.unitIndex >= line.quantity) {
        throw new Error('ORDER_PRICING_ADJUSTMENT_INVALID');
      }
    }
  }

  const calculatedTotal =
    pricing.lines.reduce((sum, line) => sum + line.lineSubtotalMinor, 0) +
    pricing.adjustments.reduce((sum, adjustment) => sum + adjustment.amountMinor, 0);
  if (!Number.isSafeInteger(pricing.totalMinor) || pricing.totalMinor < 0 || pricing.totalMinor !== calculatedTotal) {
    throw new Error('ORDER_PRICING_TOTAL_MISMATCH');
  }
};

const resolvePrice = (
  intake: OrderIntakeEnvelope,
  pricing: PricingEvidence,
  policy?: PriceDifferencePolicy,
  decision?: PriceDifferenceDecision,
): PriceResolution => {
  const displayed = intake.customerSubmission?.displayedTotalMinor;
  if (displayed === undefined) {
    return { authoritativeTotalMinor: pricing.totalMinor, differenceMinor: 0, finalTotalMinor: pricing.totalMinor, action: 'matched' };
  }
  if (intake.customerSubmission?.currency !== pricing.currency) throw new Error('ORDER_CUSTOMER_CURRENCY_MISMATCH');
  const difference = pricing.totalMinor - displayed;
  if (difference === 0) {
    return {
      customerDisplayedTotalMinor: displayed,
      authoritativeTotalMinor: pricing.totalMinor,
      differenceMinor: 0,
      finalTotalMinor: pricing.totalMinor,
      action: 'matched',
    };
  }
  if (!policy) throw new Error('ORDER_PRICE_DIFFERENCE_POLICY_REQUIRED');
  if (!decision) throw new Error('ORDER_PRICE_DIFFERENCE_REQUIRES_RESOLUTION');
  if (!decision.operatorId || !Number.isFinite(Date.parse(decision.resolvedAt))) {
    throw new Error('ORDER_PRICE_DECISION_AUDIT_REQUIRED');
  }

  if (decision.action === 'store_accepted_displayed') {
    if (policy.mode !== 'store_may_accept_displayed') throw new Error('ORDER_PRICE_DIFFERENCE_CONTACT_REQUIRED');
    if (!Number.isSafeInteger(policy.maxAcceptedDifferenceMinor) || policy.maxAcceptedDifferenceMinor < 0 ||
        Math.abs(difference) > policy.maxAcceptedDifferenceMinor) {
      throw new Error('ORDER_PRICE_DIFFERENCE_EXCEEDS_LIMIT');
    }
    return {
      customerDisplayedTotalMinor: displayed,
      authoritativeTotalMinor: pricing.totalMinor,
      differenceMinor: difference,
      finalTotalMinor: displayed,
      action: decision.action,
      operatorId: decision.operatorId,
      resolvedAt: decision.resolvedAt,
      reason: decision.reason,
    };
  }

  return {
    customerDisplayedTotalMinor: displayed,
    authoritativeTotalMinor: pricing.totalMinor,
    differenceMinor: difference,
    finalTotalMinor: pricing.totalMinor,
    action: decision.action,
    operatorId: decision.operatorId,
    resolvedAt: decision.resolvedAt,
    reason: decision.reason,
  };
};

export function createOrderFinalizer() {
  const byIdempotency = new Map<string, StoredFinalization>();
  const byIntake = new Map<string, StoredFinalization>();
  const byOrder = new Map<string, StoredFinalization>();

  return {
    finalize(
      intake: OrderIntakeEnvelope,
      evidence: OrderFinalizationEvidence,
      command: FinalizeOrderCommand,
    ): CanonicalOrderSnapshot {
      if (!command.orderId || !command.idempotencyKey || !Number.isFinite(Date.parse(command.createdAt))) {
        throw new Error('ORDER_FINALIZATION_IDENTITY_REQUIRED');
      }
      const customerId = command.customerId?.trim();
      if (command.customerId !== undefined && !customerId) throw new Error('ORDER_CUSTOMER_ID_INVALID');
      assertAcceptedIntake(intake);

      const signature = signatureOf(intake, evidence, command);
      const replay = byIdempotency.get(command.idempotencyKey);
      if (replay) {
        if (replay.signature !== signature) throw new Error('ORDER_FINALIZATION_IDEMPOTENCY_CONFLICT');
        return cloneOrder(replay.order);
      }
      if (byIntake.has(intake.intakeId) || byOrder.has(command.orderId)) throw new Error('CANONICAL_ORDER_CONFLICT');

      assertAuthorityEvidence(evidence);
      assertAvailability(intake, evidence.validation);
      assertPricing(intake, evidence.pricing);
      const priceResolution = resolvePrice(intake, evidence.pricing, command.pricePolicy, command.priceDecision);

      const order: CanonicalOrderSnapshot = {
        orderId: command.orderId,
        intakeId: intake.intakeId,
        idempotencyKey: command.idempotencyKey,
        revision: 1,
        status: 'accepted',
        businessDate: intake.businessDate,
        source: intake.source,
        channel: intake.channel,
        ...(customerId ? { customerId } : {}),
        serviceMode: intake.serviceMode,
        lineServiceModes: intake.lineServiceModes?.map((entry) => ({ ...entry })),
        lines: intake.lines.map(cloneLine),
        currency: evidence.pricing.currency,
        totalMinor: priceResolution.finalTotalMinor,
        pricingLines: evidence.pricing.lines.map((line) => ({ ...line })),
        pricingAdjustments: evidence.pricing.adjustments.map(cloneAdjustment),
        priceResolution,
        authority: authorityRevisionsFromEvidence(evidence),
        createdAt: command.createdAt,
      };

      const stored = { signature, order };
      byIdempotency.set(command.idempotencyKey, stored);
      byIntake.set(intake.intakeId, stored);
      byOrder.set(command.orderId, stored);
      return cloneOrder(order);
    },

    list(): readonly CanonicalOrderSnapshot[] {
      return [...byOrder.values()].map((stored) => cloneOrder(stored.order));
    },

    get(orderId: string): CanonicalOrderSnapshot | null {
      const stored = byOrder.get(orderId);
      return stored ? cloneOrder(stored.order) : null;
    },
  };
}
